import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { getIncentiveLeaderboard, getIncentiveLeads, type IncentiveDetailRequest } from '@/services/reportsService';
import { toastMessage } from '@/utils/toast-message';
import { downloadCSV, type CsvColumn } from '@/utils/csv';
import IncentiveOrdersModal from '@/components/reports/IncentiveOrdersModal';
import IncentiveLeadsModal from '@/components/reports/IncentiveLeadsModal';
import {
  IndianRupee, RefreshCw, Trophy,
  Package, Truck, ChevronUp, ChevronDown, ChevronsUpDown, Info,
  Download, Users, AlertTriangle,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface IncentiveRow {
  user_id: number;
  user_uuid: string;
  agent_name: string;
  agent_email: string;
  agent_code: string;
  leads: number;
  orders: number;
  revenue: number;
  aov: number;
  conversion_pct: number;
  units: number;
  cod_orders: number;
  partial_orders: number;
  prepaid_orders: number;
  voided_orders: number;
  delivered_count: number;
  in_transit_count: number;
  rto_count: number;
  fulfilled_count: number;
  rto_pct: number;
  rto_blocked: boolean;
  /** false = the agent has left. Their rows still count, so the total ties out. */
  is_active: boolean;
}

/** One line of revenue that could not be pinned on any agent. */
interface UnattributedRow {
  reason: 'tag_unmatched' | 'web_agent_lead' | 'web_unassigned' | 'web_no_lead';
  orders: number;
  revenue: number;
  units: number;
  cod_orders: number;
  partial_orders: number;
  prepaid_orders: number;
  voided_orders: number;
  delivered_count: number;
  in_transit_count: number;
  rto_count: number;
}

/** Server-side totals: agents + unattributed. Never reduce these in the browser. */
interface IncentiveTotals {
  orders: number; revenue: number; units: number; leads: number; aov: number;
  cod_orders: number; partial_orders: number; prepaid_orders: number; voided_orders: number;
  delivered_count: number; in_transit_count: number; rto_count: number;
  attributed_orders: number; attributed_revenue: number;
  unattributed_orders: number; unattributed_revenue: number;
}

/** What each unattributed line means, in the words an agent lead would use. */
const REASON_LABEL: Record<UnattributedRow['reason'], { label: string; hint: string; warn?: boolean }> = {
  tag_unmatched:  { label: 'Tag not recognised', warn: true,
                    hint: 'The order carries a sales tag that matches no agent — usually a misspelling, e.g. a space or the wrong case. These are fixable: correct the tag in Shopify or add it to the agent.' },
  web_agent_lead: { label: 'Website · agent owns the lead',
                    hint: 'Bought on the website, but the customer is assigned to an agent. No tag is possible because the agent did not raise the order; only their coupon code could have credited it.' },
  web_unassigned: { label: 'Website · lead unassigned',
                    hint: 'Bought on the website and nobody owns the customer in the CRM.' },
  web_no_lead:    { label: 'Website · no CRM lead',
                    hint: 'Bought on the website and there is no CRM lead for this customer at all.' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().split('T')[0];
const monthStart = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

const fmtINR = (v: number) =>
  `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;


// ─── Component ────────────────────────────────────────────────────────────────

const IncentiveLeaderboard = () => {
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [bucket, setBucket] = useState<'' | 'abandoned_cart' | 'reorder' | 'rto' | 'none'>('');
  const [agentSearch, setAgentSearch] = useState('');
  const [rows, setRows] = useState<IncentiveRow[]>([]);
  const [unattributed, setUnattributed] = useState<UnattributedRow[]>([]);
  const [totals, setTotals] = useState<IncentiveTotals | null>(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [sortCol, setSortCol] = useState<keyof IncentiveRow>('revenue');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Drill-down modals
  const [ordersModal, setOrdersModal] = useState<{ open: boolean; title: string; req: IncentiveDetailRequest }>({ open: false, title: '', req: { from_date: '', to_date: '' } });
  const [leadsModal, setLeadsModal] = useState<{ open: boolean; title: string; req: IncentiveDetailRequest }>({ open: false, title: '', req: { from_date: '', to_date: '' } });

  // Export dropdown
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) setExportOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const PINNED_BOTTOM = new Set(['payal.t@zeroharm.in', 'meena.reddy20zeroharm@gmail.com']);

  const filteredRows = useMemo(() => {
    const q = agentSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.agent_name || '').toLowerCase().includes(q) ||
      (r.agent_code || '').toLowerCase().includes(q) ||
      (r.agent_email || '').toLowerCase().includes(q)
    );
  }, [rows, agentSearch]);

  const sortedRows = useMemo(() => {
    const main = filteredRows.filter(r => !PINNED_BOTTOM.has(r.agent_email));
    const pinned = filteredRows.filter(r => PINNED_BOTTOM.has(r.agent_email));
    const sorted = [...main].sort((a, b) => {
      const av = a[sortCol], bv = b[sortCol];
      if (typeof av === 'string' && typeof bv === 'string')
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
    return [...sorted, ...pinned];
  }, [filteredRows, sortCol, sortDir]);

  const handleSort = (col: keyof IncentiveRow) => {
    if (col === sortCol) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getIncentiveLeaderboard({ from_date: fromDate, to_date: toDate, lead_bucket: bucket || null });
      if (res.success) {
        const body = res as unknown as {
          data: IncentiveRow[]; unattributed?: UnattributedRow[]; totals?: IncentiveTotals;
        };
        setRows(body.data || []);
        setUnattributed(body.unattributed || []);
        setTotals(body.totals || null);
        setLoaded(true);
      } else {
        toastMessage('Failed to load incentive data', 'error');
      }
    } catch {
      toastMessage('Error loading incentive leaderboard', 'error');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, bucket]);

  // Base request for drill-downs — always reflects the current report filters.
  const baseReq = (): IncentiveDetailRequest => ({
    from_date: fromDate,
    to_date: toDate,
    lead_bucket: bucket || null,
  });

  const openAgentOrders = (
    agentUuid: string,
    agentName: string,
    label: string,
    payment_status?: IncentiveDetailRequest['payment_status'],
    delivery_status?: IncentiveDetailRequest['delivery_status'],
  ) => {
    setOrdersModal({
      open: true,
      title: `${agentName} — ${label}`,
      req: { ...baseReq(), agent_uuid: agentUuid, payment_status, delivery_status },
    });
  };

  const openAllOrders = (
    label: string,
    payment_status?: IncentiveDetailRequest['payment_status'],
    delivery_status?: IncentiveDetailRequest['delivery_status'],
  ) => {
    setOrdersModal({
      open: true,
      title: `All Agents — ${label}`,
      req: { ...baseReq(), payment_status, delivery_status },
    });
  };

  const openAgentLeads = (agentUuid: string, agentName: string) => {
    setLeadsModal({
      open: true,
      title: `${agentName} — Leads`,
      req: { ...baseReq(), agent_uuid: agentUuid },
    });
  };

  const openAllLeads = () => {
    setLeadsModal({ open: true, title: 'All Agents — Leads', req: baseReq() });
  };

  // Export the on-screen leaderboard table (current sorted/filtered rows).
  const exportTable = () => {
    if (sortedRows.length === 0) { toastMessage('Nothing to export', 'error'); return; }
    const cols: CsvColumn<IncentiveRow>[] = [
      { header: 'Agent', accessor: r => r.agent_name || '' },
      { header: 'Agent Code', accessor: r => r.agent_code || '' },
      { header: 'Email', accessor: r => r.agent_email || '' },
      { header: 'Leads', accessor: 'leads' },
      { header: 'Orders', accessor: 'orders' },
      { header: 'Conv%', accessor: r => r.conversion_pct.toFixed(1) },
      { header: 'Revenue', accessor: 'revenue' },
      { header: 'AOV', accessor: 'aov' },
      { header: 'Units', accessor: 'units' },
      { header: 'COD', accessor: 'cod_orders' },
      { header: 'Partial', accessor: 'partial_orders' },
      { header: 'Prepaid', accessor: 'prepaid_orders' },
      { header: 'Voided', accessor: 'voided_orders' },
      { header: 'Pending', accessor: r => r.orders - r.delivered_count - r.in_transit_count - r.rto_count },
      { header: 'In-Transit', accessor: 'in_transit_count' },
      { header: 'Delivered', accessor: 'delivered_count' },
      { header: 'RTO', accessor: 'rto_count' },
      { header: 'RTO%', accessor: r => r.rto_pct.toFixed(1) },
    ];
    downloadCSV(`incentive_leaderboard_${fromDate}_to_${toDate}`, cols, sortedRows);
    setExportOpen(false);
  };

  // Export the full leads list across all agents (current filters).
  const exportAllLeads = async () => {
    setExportOpen(false);
    try {
      const res = await getIncentiveLeads(baseReq());
      const r = res as { success?: boolean; data?: Array<Record<string, unknown>> } | null;
      if (!r?.success || !r.data) { toastMessage('Failed to load leads for export', 'error'); return; }
      if (r.data.length === 0) { toastMessage('No leads to export', 'error'); return; }
      const fmtDate = (s: unknown) => (typeof s === 'string' ? s.split('T')[0] : '');
      const cols: CsvColumn<Record<string, unknown>>[] = [
        { header: 'Name', accessor: r => r.name || '' },
        { header: 'Phone', accessor: r => r.phone || '' },
        { header: 'Email', accessor: r => r.email || '' },
        { header: 'Bucket', accessor: r => r.lead_bucket || 'No Bucket' },
        { header: 'Agent', accessor: r => r.agent_name || '' },
        { header: 'Owner', accessor: r => r.owner_name || '' },
        { header: 'Last Remark', accessor: r => r.last_remark || '' },
        { header: 'Last Remark At', accessor: r => fmtDate(r.last_remark_at) },
        { header: 'Created Date', accessor: r => fmtDate(r.created_date) },
      ];
      downloadCSV(`incentive_all_leads_${fromDate}_to_${toDate}`, cols, r.data);
    } catch {
      toastMessage('Error exporting leads', 'error');
    }
  };

  // Card figures come from the server, which sums agents AND unattributed.
  // They used to be a reduce over `filteredRows`, which both excluded every
  // unattributed order -- 80% of revenue -- and silently followed the agent
  // search box, so typing a name changed what looked like a company total.
  // The search now filters the table only; the cards stay the company figure.
  const cards = {
    leads: totals?.leads ?? 0,
    orders: totals?.orders ?? 0,
    revenue: totals?.revenue ?? 0,
    units: totals?.units ?? 0,
    delivered: totals?.delivered_count ?? 0,
    in_transit: totals?.in_transit_count ?? 0,
    rto: totals?.rto_count ?? 0,
  };

  const overallConvPct = cards.leads > 0
    ? ((totals?.attributed_orders ?? 0) / cards.leads * 100).toFixed(1)
    : '0.0';
  const overallAov = totals?.aov ?? 0;

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* ── Header ── */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-amber-500 p-2 rounded-lg">
              <IndianRupee className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                Agent Leaderboard
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Order attribution by agent tag and discount code
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-500 dark:text-gray-400">Agent</label>
              <input
                type="text"
                value={agentSearch}
                onChange={e => setAgentSearch(e.target.value)}
                placeholder="Search agent…"
                className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor w-40"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-500 dark:text-gray-400">Bucket</label>
              <div className="relative group flex items-center">
                <Info className="h-3.5 w-3.5 text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400 cursor-help transition-colors" />
                <div className="absolute top-full left-0 mt-2 w-64 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 leading-relaxed shadow-lg">
                  <div className="absolute bottom-full left-2 border-4 border-transparent border-b-gray-900 dark:border-b-gray-700" />
                  Filters the whole report to one pipeline. <b>No Bucket</b> = leads that never entered the Abandoned Cart, Reorder, or RTO pipelines — e.g. agent-created or direct phone/web leads (and the orders linked to them).
                </div>
              </div>
              <select
                value={bucket}
                onChange={e => setBucket(e.target.value as typeof bucket)}
                className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor"
              >
                <option value="">All Buckets</option>
                <option value="abandoned_cart">Abandoned Cart</option>
                <option value="reorder">Reorder</option>
                <option value="rto">RTO</option>
                <option value="none">No Bucket</option>
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-500 dark:text-gray-400">From</label>
              <input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-500 dark:text-gray-400">To</label>
              <input
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor"
              />
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-1.5 bg-brandColor text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span>{loading ? 'Loading…' : 'Run'}</span>
            </button>

            {/* Export dropdown */}
            {loaded && (
              <div className="relative" ref={exportRef}>
                <button
                  onClick={() => setExportOpen(o => !o)}
                  className="flex items-center space-x-2 px-4 py-1.5 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  <span>Export</span>
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                {exportOpen && (
                  <div className="absolute right-0 mt-1 w-52 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 py-1">
                    <button onClick={exportTable} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <Trophy className="h-4 w-4 text-amber-500" /> Leaderboard table
                    </button>
                    <button onClick={exportAllLeads} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <Users className="h-4 w-4 text-rose-500" /> All leads (CSV)
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 overflow-auto p-6 space-y-6">

        {/* Summary Cards */}
        {loaded && (() => {
          const pending = cards.orders - cards.delivered - cards.in_transit - cards.rto;
          const p = (n: number) => cards.orders > 0 ? ((n / cards.orders) * 100).toFixed(1) : '0.0';
          return (
            <div className="grid grid-cols-5 gap-4">
              <StatCard label="Total Leads"  value={cards.leads.toLocaleString('en-IN')}  color="border-rose-500"    info="Leads created and assigned to agents in the selected date range." onClick={openAllLeads} />
              <StatCard label="Conv%"        value={`${overallConvPct}%`}                  color="border-violet-500"  info="Agent-attributed orders ÷ leads × 100. Only attributed orders are counted here — a website order nobody was credited for is not a conversion of anyone\u2019s lead." />
              <StatCard label="Total Orders" value={cards.orders.toLocaleString('en-IN')} color="border-indigo-500"  info="Every order in the range, whether or not it could be credited to an agent. The rows below split it: agents first, then one line per reason an order could not be attributed." onClick={() => openAllOrders('Orders')} />
              <StatCard label="Total Units"  value={cards.units.toLocaleString('en-IN')}  color="border-orange-500"  info="Sum of all line item quantities across every order in the range." />
              <StatCard label="Total Revenue" value={fmtINR(cards.revenue)}                color="border-green-600"   info="Total order value (total_price) of every order in the range. Matches the Orders report for the same dates." onClick={() => openAllOrders('Orders')} />
              <StatCard label="AOV"          value={fmtINR(overallAov)}                    color="border-teal-500"    info="Average Order Value = Revenue ÷ Total Orders." />
              <StatCard label="Delivered"  value={cards.delivered.toLocaleString('en-IN')}  sub={`${p(cards.delivered)}% of orders`}  color="border-emerald-500" info="Orders with Clickpost status 8 (Delivered) or 48 (Partial Delivered)." onClick={() => openAllOrders('Delivered', undefined, 'delivered')} />
              <StatCard label="In-Transit" value={cards.in_transit.toLocaleString('en-IN')} sub={`${p(cards.in_transit)}% of orders`} color="border-sky-500"     info="Orders with Clickpost status 4 (Picked Up), 5 (In Transit), 6 (Out for Delivery), 18 (Delayed), or 28 (AWB Registered)." onClick={() => openAllOrders('In-Transit', undefined, 'in_transit')} />
              <StatCard label="RTO"        value={cards.rto.toLocaleString('en-IN')}        sub={`${p(cards.rto)}% of orders`}        color="border-red-500"     info="Orders with Clickpost status 9 (Failed Delivery), 10 (Cancelled), 11 (RTO Requested), 12 (RTO Marked), 14 (RTO Delivered), or 21 (RTO In Transit)." onClick={() => openAllOrders('RTO', undefined, 'rto')} />
              <StatCard label="Pending"    value={pending.toLocaleString('en-IN')}            sub={`${p(pending)}% of orders`}           color="border-gray-400"   info="Orders with no Clickpost status yet — placed but not yet picked up or scanned by the courier." onClick={() => openAllOrders('Pending', undefined, 'pending')} />
            </div>
          );
        })()}

        {/* Empty / loading state */}
        {!loaded && !loading && (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-500">
            <Trophy className="h-12 w-12 mb-3 opacity-30" />
            <p className="text-sm">Select a date range and click Run to load the leaderboard.</p>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-500">
            <RefreshCw className="h-8 w-8 mb-3 animate-spin opacity-50" />
            <p className="text-sm">Loading incentive data…</p>
          </div>
        )}

        {/* Table */}
        {loaded && !loading && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <Th>#</Th>
                    <SortTh col="agent_name" label="Agent" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                    <SortTh col="leads" label="Leads" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                    <SortTh col="orders" label="Orders" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                    <SortTh col="conversion_pct" label="Conv%" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                    <SortTh col="revenue" label="Revenue" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                    <SortTh col="aov" label="AOV" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                    <SortTh col="units" label="Units" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                    <Th>Payment Split</Th>
                    <Th>Delivery Status</Th>
                    <SortTh col="rto_pct" label="RTO%" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {sortedRows.map((row, idx) => (
                    <AgentRow
                      key={row.user_id}
                      row={row}
                      rank={idx + 1}
                      onViewLeads={() => openAgentLeads(row.user_uuid, row.agent_name)}
                      onPayment={(status, label) => openAgentOrders(row.user_uuid, row.agent_name, label, status)}
                      onDelivery={(status, label) => openAgentOrders(row.user_uuid, row.agent_name, label, undefined, status)}
                    />
                  ))}
                  {sortedRows.length === 0 && (
                    <tr>
                      <td colSpan={11} className="py-10 text-center text-gray-400 dark:text-gray-500 text-sm">
                        {rows.length === 0
                          ? 'No data found for the selected period.'
                          : 'No agents match your search.'}
                      </td>
                    </tr>
                  )}

                  {/* Revenue that could not be pinned on an agent. Kept out of
                      sortedRows and rendered after it, the same way PINNED_BOTTOM
                      works, so these never take a rank or a medal. Without them
                      the page showed 21% of revenue and called it the total. */}
                  {loaded && unattributed.map((u) => {
                    const meta = REASON_LABEL[u.reason];
                    const pending = u.orders - u.delivered_count - u.in_transit_count - u.rto_count;
                    return (
                      <tr
                        key={u.reason}
                        title={meta?.hint}
                        className={meta?.warn
                          ? 'bg-amber-50/70 dark:bg-amber-900/10 border-t border-amber-200 dark:border-amber-900/40'
                          : 'bg-gray-50/70 dark:bg-gray-900/20 border-t border-gray-200 dark:border-gray-700'}
                      >
                        <td className="px-4 py-3 text-center">
                          {meta?.warn
                            ? <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 inline" />
                            : <span className="text-gray-300 dark:text-gray-600">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <p className={`text-sm font-medium ${meta?.warn
                            ? 'text-amber-900 dark:text-amber-300'
                            : 'text-gray-600 dark:text-gray-400'}`}>
                            {meta?.label ?? u.reason}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">not credited to an agent</p>
                        </td>
                        <td className="px-4 py-3 text-left text-gray-300 dark:text-gray-600">—</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{u.orders.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 text-gray-300 dark:text-gray-600">—</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{fmtINR(u.revenue)}</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                          {u.orders > 0 ? fmtINR(Math.round(u.revenue / u.orders)) : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{u.units.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                          <div>COD {u.cod_orders.toLocaleString('en-IN')}</div>
                          <div>Partial {u.partial_orders.toLocaleString('en-IN')}</div>
                          <div>Prepaid {u.prepaid_orders.toLocaleString('en-IN')}</div>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                          <div>Pending {pending.toLocaleString('en-IN')}</div>
                          <div>In-Transit {u.in_transit_count.toLocaleString('en-IN')}</div>
                          <div>Delivered {u.delivered_count.toLocaleString('en-IN')}</div>
                          <div>RTO {u.rto_count.toLocaleString('en-IN')}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                          {u.orders > 0 ? `${((u.rto_count / u.orders) * 100).toFixed(1)}%` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

                {/* Grand total. Ties to the Orders report for the same dates --
                    that is the point of the two lines above it. */}
                {loaded && totals && (
                  <tfoot>
                    <tr className="bg-gray-100 dark:bg-gray-700 border-t-2 border-gray-300 dark:border-gray-500 font-semibold text-gray-900 dark:text-white">
                      <td className="px-4 py-3" />
                      <td className="px-4 py-3 text-xs font-bold uppercase tracking-wide">
                        Total
                        <p className="text-[11px] font-normal normal-case text-gray-500 dark:text-gray-400">
                          every order in the range
                        </p>
                      </td>
                      <td className="px-4 py-3 text-left">{totals.leads.toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3">{totals.orders.toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-gray-400 dark:text-gray-500">—</td>
                      <td className="px-4 py-3">{fmtINR(totals.revenue)}</td>
                      <td className="px-4 py-3">{fmtINR(totals.aov)}</td>
                      <td className="px-4 py-3">{totals.units.toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3" />
                      <td className="px-4 py-3" />
                      <td className="px-4 py-3" />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}

        {/* Legend */}
        {loaded && (
          <div className="flex items-start space-x-6 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center space-x-1.5">
              <Truck className="h-3 w-3" />
              <span>Payment: COD - Partial - Prepaid (as %)</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <Package className="h-3 w-3" />
              <span>Delivery: Pending - InTransit - Delivered - RTO (as %)</span>
            </div>
          </div>
        )}
      </div>

      {/* Drill-down modals */}
      <IncentiveOrdersModal
        isOpen={ordersModal.open}
        onClose={() => setOrdersModal(m => ({ ...m, open: false }))}
        title={ordersModal.title}
        request={ordersModal.req}
      />
      <IncentiveLeadsModal
        isOpen={leadsModal.open}
        onClose={() => setLeadsModal(m => ({ ...m, open: false }))}
        title={leadsModal.title}
        request={leadsModal.req}
      />
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const Th = ({ children }: { children: React.ReactNode }) => (
  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
    {children}
  </th>
);

const SortTh = ({
  col, label, sortCol, sortDir, onSort,
}: {
  col: keyof IncentiveRow;
  label: string;
  sortCol: keyof IncentiveRow;
  sortDir: 'asc' | 'desc';
  onSort: (col: keyof IncentiveRow) => void;
}) => {
  const active = col === sortCol;
  return (
    <th
      onClick={() => onSort(col)}
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none group"
    >
      <div className="flex items-center gap-1">
        <span className={active ? 'text-brandColor dark:text-blue-400' : ''}>{label}</span>
        {active
          ? sortDir === 'asc'
            ? <ChevronUp className="h-3 w-3 text-brandColor dark:text-blue-400" />
            : <ChevronDown className="h-3 w-3 text-brandColor dark:text-blue-400" />
          : <ChevronsUpDown className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
        }
      </div>
    </th>
  );
};

const StatCard = ({ label, value, sub, color, info, onClick }: { label: string; value: string; sub?: string; color: string; info?: string; onClick?: () => void }) => (
  <div
    onClick={onClick}
    className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 border-l-4 ${color} px-5 py-4 shadow-sm ${onClick ? 'cursor-pointer hover:shadow-md hover:border-gray-200 dark:hover:border-gray-600 transition-shadow' : ''}`}
  >
    <div className="flex items-center justify-between gap-1">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide truncate">{label}</p>
      {info && (
        <div className="relative group flex-shrink-0">
          <Info className="h-3.5 w-3.5 text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400 cursor-help transition-colors" />
          <div className="absolute top-full right-0 mt-2 w-52 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 leading-relaxed shadow-lg">
            <div className="absolute bottom-full right-2 border-4 border-transparent border-b-gray-900 dark:border-b-gray-700" />
            {info}
          </div>
        </div>
      )}
    </div>
    <div className="flex items-baseline justify-between mt-2 gap-2">
      <p className="text-xl font-bold text-gray-900 dark:text-white truncate">{value}</p>
      {sub && <p className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">{sub}</p>}
    </div>
  </div>
);

const SplitRow = ({ label, value, valueClass, onClick }: { label: string; value: number; valueClass: string; onClick: () => void }) => (
  <button
    onClick={onClick}
    disabled={value === 0}
    className="grid grid-cols-2 gap-x-3 w-full text-left rounded px-1 -mx-1 enabled:hover:bg-gray-100 dark:enabled:hover:bg-gray-700/50 disabled:cursor-default transition-colors"
    title={value > 0 ? `View ${label} orders` : undefined}
  >
    <span className="text-gray-400">{label}</span>
    <span className={`font-medium ${valueClass} ${value > 0 ? 'underline decoration-dotted decoration-gray-300 dark:decoration-gray-600 underline-offset-2' : ''}`}>{value}</span>
  </button>
);

type PaymentStatus = 'cod' | 'partial' | 'prepaid' | 'voided';
type DeliveryStatus = 'pending' | 'in_transit' | 'delivered' | 'rto';

const AgentRow = ({ row, rank, onViewLeads, onPayment, onDelivery }: {
  row: IncentiveRow;
  rank: number;
  onViewLeads: () => void;
  onPayment: (status: PaymentStatus, label: string) => void;
  onDelivery: (status: DeliveryStatus, label: string) => void;
}) => {
  const total = row.orders || 0;


  const isRtoHigh = row.rto_blocked;

  return (
    <tr className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/30">
      {/* Rank */}
      <td className="px-4 py-3 text-gray-400 dark:text-gray-500 font-mono text-xs">
        {rank <= 3
          ? ['🥇', '🥈', '🥉'][rank - 1]
          : rank}
      </td>

      {/* Agent */}
      <td className="px-4 py-3">
        <div className="flex items-center justify-between gap-2 group/agent">
          <div>
            <p className="font-medium text-gray-900 dark:text-white text-sm flex items-center gap-1.5">
              {row.agent_name}
              {!row.is_active && (
                <span
                  title="This agent is no longer active. Their orders still count, so the total ties out."
                  className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300"
                >
                  left
                </span>
              )}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 font-mono">{row.agent_code}</p>
          </div>
          <button
            onClick={onViewLeads}
            title="View leads for this agent"
            className="flex-shrink-0 flex items-center gap-1 text-xs text-brandColor dark:text-blue-400 opacity-0 group-hover/agent:opacity-100 transition-opacity hover:underline"
          >
            <Users className="h-3.5 w-3.5" />
            Leads
          </button>
        </div>
      </td>

      {/* Leads */}
      <td className="px-4 py-3 text-left">
        <button onClick={onViewLeads} className="text-gray-700 dark:text-gray-300 hover:text-brandColor dark:hover:text-blue-400 hover:underline">
          {row.leads}
        </button>
      </td>

      {/* Orders */}
      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 text-left font-medium">{row.orders}</td>

      {/* Conv% */}
      <td className="px-4 py-3 text-left">
        <span className={`text-xs font-semibold ${
          row.conversion_pct >= 20
            ? 'text-green-600 dark:text-green-400'
            : row.conversion_pct >= 10
              ? 'text-yellow-600 dark:text-yellow-400'
              : 'text-gray-500 dark:text-gray-400'
        }`}>
          {row.conversion_pct.toFixed(1)}%
        </span>
      </td>

      {/* Revenue */}
      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 text-left font-medium">
        {fmtINR(row.revenue)}
      </td>

      {/* AOV */}
      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-left">
        {fmtINR(row.aov)}
      </td>

      {/* Units */}
      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 text-left">{row.units}</td>

      {/* Payment Split */}
      <td className="px-4 py-3">
        <div className="text-xs space-y-0.5">
          <SplitRow label="COD" value={row.cod_orders} valueClass="text-blue-600 dark:text-blue-400" onClick={() => onPayment('cod', 'COD Orders')} />
          <SplitRow label="Partial" value={row.partial_orders} valueClass="text-yellow-600 dark:text-yellow-400" onClick={() => onPayment('partial', 'Partial-Paid Orders')} />
          <SplitRow label="Prepaid" value={row.prepaid_orders} valueClass="text-green-600 dark:text-green-400" onClick={() => onPayment('prepaid', 'Prepaid Orders')} />
          {(row.voided_orders || 0) > 0 && (
            <SplitRow label="Voided" value={row.voided_orders} valueClass="text-gray-400 dark:text-gray-500" onClick={() => onPayment('voided', 'Voided Orders')} />
          )}
        </div>
      </td>

      {/* Delivery Status */}
      <td className="px-4 py-3">
        <div className="text-xs space-y-0.5">
          <SplitRow label="Pending" value={total - row.delivered_count - row.in_transit_count - row.rto_count} valueClass="text-gray-400 dark:text-gray-500" onClick={() => onDelivery('pending', 'Pending Delivery')} />
          <SplitRow label="In-Transit" value={row.in_transit_count} valueClass="text-blue-600 dark:text-blue-400" onClick={() => onDelivery('in_transit', 'In-Transit Orders')} />
          <SplitRow label="Delivered" value={row.delivered_count} valueClass="text-green-600 dark:text-green-400" onClick={() => onDelivery('delivered', 'Delivered Orders')} />
          <SplitRow label="RTO" value={row.rto_count} valueClass="text-red-500" onClick={() => onDelivery('rto', 'RTO Orders')} />
        </div>
      </td>

      {/* RTO% */}
      <td className="px-4 py-3 text-left">
        <span className={`text-sm font-semibold ${
          isRtoHigh
            ? 'text-red-600 dark:text-red-400'
            : row.rto_pct > 10
              ? 'text-orange-500'
              : 'text-gray-600 dark:text-gray-400'
        }`}>
          {row.rto_pct.toFixed(1)}%
        </span>
      </td>

    </tr>
  );
};

export default IncentiveLeaderboard;
